import { EmberAnxietyState } from "game-common/models"
import { Container, Graphics, Text } from "pixi.js"
import { TextRenderer } from "../text/text_renderer"
import { dialFaceRadius, drawDialGauge } from "./dial"
import { blendColor } from "./flare"

// The readout the player carries down into the cave: how the lord FEELS back at the camp, never the
// danger of the cave the player is standing in. It is the only thread back to the surface.
//
// Built as a SENSE rather than as an instrument, which is a deliberate break from the HUD's other
// round widgets. The countdown dial and the wave badge measure things -- seconds, enemies -- and
// wear the hardware to prove it: a groove, a rim, a bright cursor, a numeral in the middle. This one
// is a feeling arriving from somewhere the player cannot see, so it has no number to read, no trace
// to follow and no ticks to count against. What it has is:
//
//   1. A HEART that beats, at his rate: ~43bpm settled against ~144bpm desperate. Nothing else on
//      the HUD moves at a tempo, which is what makes it legible out of the corner of an eye -- and
//      what makes it unmistakable when it STOPS.
//   2. A pulse of light off each beat, and a ring of colour that fills as he worsens: how far gone
//      he is, as an amount of light rather than as a position on a scale.
//   3. The CAUSE -- the server's own sentence, and the only text on the widget. There is no band
//      word: naming the four moods turned a feeling back into a category being reported, and "The
//      cold is getting in." already carries both the trouble and the tone of it. The band survives
//      as the COLOUR of everything, which is how a mood is worn rather than announced.
//
// Everything is plain fills and strokes. No filters: a soft edge here is a fatter, fainter copy of
// the same path, because the hardware this game targets cannot spare a filter pass on the HUD.

const PLATE = { width: 278, height: 58, corner: 7 }
const PLATE_COLOR = 0x05070a // the HUD chip plate (ember_players_chip, ember_minimap_chip)
const PLATE_ALPHA = 0.82

const SOCKET = { radius: 18, strokeWidth: 4, x: 31, y: 29 }
const COLUMN_X = 58 // left edge of the text column, clear of the socket

const CAUSE_COLOR = 0xc9d2d9
const TITLE_COLOR = 0x8b98a5
const FALLEN_COLOR = 0x7d8c9b // cold slate: the one colour on the widget that is not a mood

// Brightened a touch from the server's own bands so they survive being used as a TINT, which can
// only ever darken white text.
const BANDS: Record<EmberAnxietyState["band"], number> = {
    settled: 0x8fd6ad,
    uneasy: 0xe8c874,
    afraid: 0xf09a55,
    desperate: 0xf2676a,
}

// The small-caps line above the sentence. It never names a mood -- it names what is being felt, and
// the one thing a mood cannot say for itself: that there is nothing left to feel.
const TITLE = "THE LORD'S HEART"
const TITLE_FALLEN = "THE LORD HAS FALLEN"
const BAND_ORDER: EmberAnxietyState["band"][] = ["settled", "uneasy", "afraid", "desperate"]

const clamp01 = (value: number): number => Math.max(0, Math.min(1, value))

/**
 * How tall the plate has to be to hold a cause whose last line ends at `causeBottom`.
 *
 * Every sentence the server sends today is one line with room to spare (the longest, "He braces for
 * the next assault.", measures 191 of the 208 it is given), so this is the fixed height in practice.
 * It is computed rather than fixed because the two ways that stops being true are both silent: a
 * longer sentence added server-side, and the webfont landing AFTER the first reading does, which
 * remeasures every line under the widget's feet. Either one would otherwise put the cause's second
 * line below the bottom of the plate, on the bare world.
 */
export const plateHeightFor = (causeBottom: number): number => Math.max(PLATE.height, Math.round(causeBottom + 10))

// ===== the beat =======================================================================

// One contraction, measured in SECONDS SINCE THE BEAT BEGAN rather than as a fraction of the gap to
// the next one. A heart's squeeze takes about as long whatever the rate -- what shortens when it
// races is the pause after it, and keeping that true is what stops a desperate lord's heart reading
// as a fast flutter instead of as hard, close thumps.
const bump = (t: number, from: number, to: number, height: number): number =>
    t <= from || t >= to ? 0 : Math.sin((Math.PI * (t - from)) / (to - from)) * height

/** How much the heart is squeezed (0..1.4) at `t` seconds into a beat: a thump, then a lesser one. */
export const pulseSqueeze = (t: number): number => bump(t, 0.07, 0.2, 1) + bump(t, 0.21, 0.32, 0.4)

/** Beats per second for an anxiety of 0..100: ~43bpm settled, ~144bpm desperate. */
export const beatsPerSecond = (value: number): number => 0.72 + 1.68 * clamp01(value / 100)

/** How long the ring of light thrown off by a beat lives, in seconds. */
const HALO_SECONDS = 0.55

/** A compact readout of the lord's feeling, never the local cave's danger. */
export class EmberAnxietyMeter extends Container {
    // Everything visible hangs off `body`, so a desperate lord's tremble can shift it without
    // touching the widget's own x/y -- which the GUI sets afresh every frame.
    private body = new Container()
    private plateGlow = new Graphics()
    private plate = new Graphics()
    private socketGlow = new Graphics()
    private socket = new Graphics()
    private gauge = new Graphics()
    private heart = new Graphics()
    private title: Text
    private cause: Text

    private key = ""
    private plateHeight = PLATE.height
    private band: EmberAnxietyState["band"] = "settled"
    private fallen = false
    private target = 0
    private shown = -1 // < 0 = no reading yet, so the first one lands without a sweep
    private drawnValue = -1
    private drawnStyle = ""

    // The widget's own clock, in seconds. Only advanced while the meter is up, so a trip back to the
    // surface doesn't leave a gap in the beat.
    private clock = 0
    private nextBeat = 0
    private sinceBeat = 99 // seconds since the last beat: what the squeeze and the halo are read from

    constructor() {
        super()
        this.addChild(this.body)
        this.body.addChild(this.plateGlow, this.plate, this.socketGlow, this.socket, this.gauge, this.heart)

        this.title = new TextRenderer().fontSize(9).bold().render(TITLE) as Text
        this.title.style.letterSpacing = 1.3 // small caps read as a label, not as a sentence
        // pixi measures the tracked string WITHOUT the trailing gap, so the canvas comes out one
        // letterSpacing short and the last glyph is cut in half -- "THE LORD'S HEAR", with a ghost
        // of the T. `padding` widens the canvas and is compensated for in the texture's trim, so
        // nothing moves.
        this.title.style.padding = 2
        this.title.tint = TITLE_COLOR
        this.cause = new TextRenderer().fontSize(11.5).wordWrap(PLATE.width - COLUMN_X - 12).render("") as Text
        this.body.addChild(this.title, this.cause)

        this.title.position.set(COLUMN_X, 13)
        this.cause.position.set(COLUMN_X, 27)

        this.heart.position.set(SOCKET.x, SOCKET.y)
        this.drawPlate()

        this.visible = false
        this.exposeDevTriggers()
    }

    /** A new reading, or null to put the meter away. */
    update(feeling: EmberAnxietyState | null) {
        this.visible = !!feeling
        if (!feeling) {
            this.key = ""
            // Climbing back out of the cave ends this reading. Keeping the needle would mean the
            // next descent opened on the LAST trip's lord, sweeping down from a fear he is no
            // longer in.
            this.shown = -1
            this.sinceBeat = 99
            return
        }
        const key = JSON.stringify(feeling)
        if (key === this.key) return
        this.key = key

        this.band = feeling.fallen ? "desperate" : feeling.band
        this.fallen = !!feeling.fallen
        this.target = Math.max(0, Math.min(100, feeling.value))
        if (this.shown < 0) this.shown = this.target

        const color = this.moodColor()
        this.title.text = this.fallen ? TITLE_FALLEN : TITLE
        this.title.tint = this.fallen ? FALLEN_COLOR : TITLE_COLOR
        this.cause.text = feeling.cause || ""
        // Tinted toward the mood, so the sentence is worn in his colour -- it is the only text left,
        // and the only place a reader can be told the difference between cold and a wound. Only
        // partway there: it still has to be read word by word.
        this.cause.tint = blendColor(CAUSE_COLOR, color, 0.4)
        this.drawPlate()
        this.drawStyle()
        // A reading can arrive with the same VALUE and a new mood -- a lord who falls at 100 is the
        // ordinary case -- so the ring is always repainted, never skipped as unchanged.
        this.drawnValue = -1
        this.drawGauge()
    }

    /**
     * Advance the beat and repaint what moves. Driven by the GUI's frame timer, and only while the
     * meter is up, so a settled camp costs one small heart and two circles a frame.
     */
    animate = (deltaMs: number) => {
        if (!this.visible) return
        const dt = Math.max(0, Math.min(0.25, (deltaMs || 0) / 1000)) // a resumed tab must not fire a burst of beats
        this.clock += dt
        this.sinceBeat += dt
        this.settleValue(dt)

        if (this.fallen) {
            // No more beats. The heart does not freeze mid-squeeze: it lets go and is still, which
            // is the whole reading -- a widget that has been pulsing for minutes simply stops.
            this.nextBeat = this.clock
            this.sinceBeat = 99
        } else {
            if (this.nextBeat < this.clock - 1) this.nextBeat = this.clock // first frame, or a long absence
            while (this.nextBeat <= this.clock) {
                this.sinceBeat = this.clock - this.nextBeat
                this.nextBeat += 1 / beatsPerSecond(this.shown)
            }
        }
        this.drawBeat(this.fallen ? 0 : pulseSqueeze(this.sinceBeat))
        this.tremble()
    }

    private moodColor = (): number => (this.fallen ? FALLEN_COLOR : BANDS[this.band])

    /** How wound up he is, 0..1 -- the dial of everything that moves. */
    private intensity = (): number => (this.fallen ? 1 : clamp01(Math.max(0, this.shown) / 100))

    // The displayed value sweeps to the server's rather than snapping to it, so a once-a-second
    // sample reads as light growing rather than as a step. Floored at a minimum step so it actually
    // LANDS: an approach that is purely proportional to the remaining distance never does.
    private settleValue = (dt: number) => {
        const distance = this.target - this.shown
        if (distance === 0) return
        const step = Math.max(0.9, Math.abs(distance) * Math.min(1, dt * 7))
        this.shown = Math.abs(distance) <= step ? this.target : this.shown + Math.sign(distance) * step
        this.drawGauge()
    }

    private drawPlate = () => {
        const height = plateHeightFor(this.cause.y + this.cause.height)
        if (height === this.plateHeight && this.plate.geometry.graphicsData.length) return
        this.plateHeight = height
        this.plate.clear()
        this.plate.beginFill(PLATE_COLOR, PLATE_ALPHA)
        this.plate.lineStyle(1, 0xffffff, 0.18)
        this.plate.drawRoundedRect(0, 0, PLATE.width, height, PLATE.corner)
        this.plate.endFill()
    }

    // The ring of colour around the heart. Redrawn only when the value has actually moved a visible
    // amount, not on every frame the heart beats on.
    private drawGauge = () => {
        const value = Math.round(Math.max(0, this.shown))
        if (value === this.drawnValue) return
        this.drawnValue = value
        drawDialGauge(this.gauge, {
            radius: SOCKET.radius,
            strokeWidth: SOCKET.strokeWidth,
            progress: value / 100,
            color: this.moodColor(),
            haloWidth: 6,
            haloAlpha: 0.2,
            // No bead: a bright cursor at the end of the arc is a reading being TAKEN, and turns the
            // ring back into the instrument this widget is trying not to be.
            bead: false,
        })
        this.gauge.position.set(SOCKET.x, SOCKET.y)
    }

    // The pool the heart sits in, and the heart itself. Both depend only on the mood, so they are
    // redrawn when it changes and merely SCALED by the beat -- the per-frame cost of a beating heart
    // is one scale.set, not a rebuilt path.
    private drawStyle = () => {
        const style = `${this.band}/${this.fallen}`
        if (style === this.drawnStyle) return
        this.drawnStyle = style

        // Concentric discs rather than a face with a rim: the heart needs somewhere dark to sit, but
        // a hard bezel around it is the first thing that would make this read as a gauge.
        this.socket.clear()
        for (const ring of [{ radius: 20, alpha: 0.1 }, { radius: 16.5, alpha: 0.14 }, { radius: 13.5, alpha: 0.2 }]) {
            this.socket.beginFill(0x000000, ring.alpha)
            this.socket.drawCircle(0, 0, ring.radius)
            this.socket.endFill()
        }
        // The unlit remainder of the ring, faint enough to be a suggestion of how much is left
        // rather than a scale with an end marked on it -- but not so faint that a settled lord's
        // short arc floats at the top of the socket with no circle to belong to.
        this.socket.lineStyle({ width: SOCKET.strokeWidth, color: this.moodColor(), alpha: 0.16 })
        this.socket.drawCircle(0, 0, SOCKET.radius - SOCKET.strokeWidth / 2)
        this.socket.position.set(SOCKET.x, SOCKET.y)

        const color = this.moodColor()
        this.heart.clear()
        // A fatter, fainter copy of the heart behind it: the only "glow" available without a filter.
        this.heart.beginFill(color, this.fallen ? 0.1 : 0.26)
        this.drawHeartPath(8.1)
        this.heart.endFill()
        this.heart.beginFill(this.fallen ? color : blendColor(color, 0xffffff, 0.25), this.fallen ? 0.5 : 1)
        this.drawHeartPath(6.9)
        this.heart.endFill()
        if (this.fallen) {
            // Broken, not merely grey and still: the crack is what says it at a glance, in the
            // half-second before anybody reads the word beside it.
            this.heart.lineStyle({ width: 1.8, color: 0x05070a, alpha: 0.95 })
            this.heart.moveTo(0.5, -8)
            this.heart.lineTo(-2.9, -2.2)
            this.heart.lineTo(2, 0.7)
            this.heart.lineTo(-1.3, 6.9)
            this.heart.lineStyle(0)
        }
    }

    private drawHeartPath = (size: number) => {
        // The classic two-lobe heart, as one closed path so the fill has no seam down the middle.
        this.heart.moveTo(0, size * 0.95)
        this.heart.bezierCurveTo(-size * 1.5, -size * 0.25, -size * 0.55, -size * 1.3, 0, -size * 0.38)
        this.heart.bezierCurveTo(size * 0.55, -size * 1.3, size * 1.5, -size * 0.25, 0, size * 0.95)
        this.heart.closePath()
    }

    // What a beat does: squeeze the heart, throw a ring of light off it, and lift the plate's own
    // glow. Together they make the whole widget breathe rather than just the icon -- and they are
    // the only motion left on it, so they carry the pulse on their own.
    private drawBeat = (squeeze: number) => {
        const intensity = this.intensity()
        this.heart.scale.set(1 + squeeze * (0.12 + 0.15 * intensity))

        const color = this.moodColor()
        const faceRadius = dialFaceRadius(SOCKET)
        this.socketGlow.clear()
        if (!this.fallen) {
            // A standing pool of light under the heart, which the beat brightens...
            this.socketGlow.beginFill(color, (0.05 + 0.09 * intensity) * (0.35 + squeeze * 0.65))
            this.socketGlow.drawCircle(0, 0, faceRadius + 3 + squeeze * 3)
            this.socketGlow.endFill()
            // ...and a ring thrown off it, starting OUTSIDE the gauge and fading before it has
            // travelled far. A ring that expands across the gauge gets read as the gauge (cf. the
            // countdown dial's ripple), so this one is never on it.
            const age = this.sinceBeat / HALO_SECONDS
            if (age < 1) {
                const fade = Math.pow(1 - age, 1.5)
                this.socketGlow.lineStyle({ width: 2, color, alpha: (0.1 + 0.14 * intensity) * fade })
                this.socketGlow.drawCircle(0, 0, faceRadius + 2 + age * 5)
                this.socketGlow.lineStyle(0)
            }
        }
        this.socketGlow.position.set(SOCKET.x, SOCKET.y)

        // A mood-coloured bloom around the plate, so a desperate camp lights its own corner of the
        // screen. Two nested rounded rects, the outer one fainter: a cheap, filterless falloff.
        const lit = this.fallen ? 0.05 : (0.05 + 0.13 * intensity * intensity) * (0.6 + squeeze * 0.4)
        this.plateGlow.clear()
        for (const ring of [{ spread: 3, alpha: lit }, { spread: 7, alpha: lit * 0.45 }]) {
            this.plateGlow.beginFill(color, ring.alpha)
            this.plateGlow.drawRoundedRect(
                -ring.spread, -ring.spread,
                PLATE.width + ring.spread * 2, this.plateHeight + ring.spread * 2,
                PLATE.corner + ring.spread,
            )
            this.plateGlow.endFill()
        }
    }

    // A fear that has nowhere left to go shows in the hand. Two sines at unrelated rates, so it
    // reads as a tremble rather than as a wobble, and stays reproducible for a test.
    private tremble = () => {
        const over = (Math.max(0, this.intensity() - 0.72) / 0.28) * (this.fallen ? 0 : 1)
        if (over <= 0) {
            this.body.position.set(0, 0)
            return
        }
        this.body.position.set(
            Math.sin(this.clock * 83.1) * 0.9 * over,
            Math.sin(this.clock * 61.7 + 1.2) * 0.7 * over,
        )
    }

    // Browser-console triggers, in the same shape as window.playersChip / window.rosterWidget:
    //
    //     anxiety(82)                      // a desperate lord, with the matching band and cause
    //     anxiety(40, "uneasy", "...")     // any band and sentence
    //     anxiety(null)                    // put it away
    //     anxietyFallen()                  // the heart stops
    //
    // Nothing a developer alone at a keyboard can do will frighten the lord on cue -- it takes a
    // wave landing on a camp the player cannot see -- so there would otherwise be no way to look at
    // the thing this widget exists to show. The next server sample corrects whatever is faked here.
    private exposeDevTriggers = () => {
        if (typeof window === "undefined") return
        const anyWindow = window as any
        anyWindow.anxietyMeter = this
        anyWindow.anxiety = (value: number | null, band?: EmberAnxietyState["band"], cause?: string): string => {
            if (value === null || value === undefined) {
                this.update(null)
                return "[anxiety] away"
            }
            const level = Math.max(0, Math.min(100, value))
            const resolved = band ?? BAND_ORDER[Math.min(3, Math.floor(level / 25))]
            this.update({ roomId: "dev", value: level, band: resolved, cause: cause ?? "The fighting is close." })
            return `[anxiety] ${level} ${resolved} @ ${(beatsPerSecond(level) * 60).toFixed(0)}bpm`
        }
        anyWindow.anxietyFallen = (): string => {
            this.update({ roomId: "dev", value: 100, band: "desperate", fallen: true, cause: "The bond falls silent." })
            return "[anxiety] fallen"
        }
    }
}
